import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Pencil, Trash2, Crown, ListChecks, LayoutGrid, Users, ArrowRight, Lock, Link2, Volume2, ClipboardList, Check, FileText, Building2, Copy, Sparkles, Smile, Frown, CloudRain, Globe, BookOpen, Printer, Mail, FolderOpen, KeyRound, Gift, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { IconRenderer } from "../lib/icons";
import { speak, listVoices } from "../lib/speech";
import Header from "../components/Header";
import Modal from "../components/Modal";
import PinModal from "../components/PinModal";
import IconPicker from "../components/IconPicker";
import CareCircleModal from "../components/CareCircleModal";

const PROFILE_COLORS = ["teal", "lavender", "peach", "yellow"];
const COLOR_BG = { teal: "#38B2AC", lavender: "#9F7AEA", peach: "#ED8936", yellow: "#ECC94B" };

function AdminGrantCard() {
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState("premium");
  const [busy, setBusy] = useState(false);
  const [grants, setGrants] = useState(null);
  const load = useCallback(() => api.get("/admin/grants").then((r) => setGrants(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/admin/grant", { email, tier });
      toast.success(data.status === "pending" ? `${data.email} will get ${data.tier} when they sign up` : `${data.email} is now ${data.tier}`);
      setEmail("");
      load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <section data-testid="admin-grant-section" className="bg-white rounded-3xl border-2 border-[#ECC94B] p-6 space-y-4">
      <p className="text-lg font-bold flex items-center gap-2"><KeyRound size={22} strokeWidth={2.5} className="text-[#B7791F]" /> Admin · grant free access</p>
      <p className="text-sm text-muted-foreground font-semibold">Give testers or partners Premium/Organisation access without paying. Works even before they sign up.</p>
      <form onSubmit={submit} className="flex flex-wrap gap-2">
        <input data-testid="admin-grant-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="tester@example.com" className="flex-1 min-w-[200px] min-h-[48px] px-4 rounded-full border-2 border-foreground/20 font-semibold" />
        <select data-testid="admin-grant-tier" value={tier} onChange={(e) => setTier(e.target.value)} className="min-h-[48px] px-4 rounded-full border-2 border-foreground/20 font-bold bg-white">
          <option value="premium">Premium</option>
          <option value="business">Organisation</option>
          <option value="free">Remove (back to free)</option>
        </select>
        <button data-testid="admin-grant-submit" disabled={busy} className="min-h-[48px] px-5 rounded-full bg-[#ECC94B] font-bold disabled:opacity-50">{busy ? "Saving…" : "Grant"}</button>
      </form>
      {grants && (grants.applied.length > 0 || grants.pending.length > 0) && (
        <ul data-testid="admin-grant-list" className="text-sm font-semibold space-y-1">
          {grants.applied.map((g) => <li key={g.email}><Check size={16} className="inline text-primary" strokeWidth={3} /> {g.email} — {g.tier}</li>)}
          {grants.pending.map((g) => <li key={g.email} className="text-muted-foreground">{g.email} — {g.tier} (waiting for sign-up)</li>)}
        </ul>
      )}
    </section>
  );
}

function LimitBar({ used, max, label, testId }) {
  return (
    <div data-testid={testId} className="text-sm font-semibold text-muted-foreground">
      {label}: <span className="text-foreground">{used} of {max}</span> used
      <div className="mt-1 h-2 rounded-full bg-secondary overflow-hidden w-40">
        <div className="h-full bg-primary rounded-full" style={{ width: `${Math.min(100, (used / max) * 100)}%` }} />
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user, isPremium, canUsePro, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState([]);
  const [activeProfile, setActiveProfile] = useState(localStorage.getItem("rb_profile") || "");
  const [routines, setRoutines] = useState([]);
  const [boards, setBoards] = useState([]);
  const [choices, setChoices] = useState([]);
  const [summary, setSummary] = useState(null);
  const [voiceRate, setVoiceRate] = useState(parseFloat(localStorage.getItem("rb_voice_rate") || "0.9"));
  const [org, setOrg] = useState(null);
  const [lastInvite, setLastInvite] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [ft, setFt] = useState({ first_label: "", first_icon: "Star", then_label: "", then_icon: "Star" });
  const [ftPickerFor, setFtPickerFor] = useState(null); // null | "first" | "then"
  const [todayMood, setTodayMood] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [stories, setStories] = useState([]);
  const [circleFor, setCircleFor] = useState(null); // profile object when open
  const [digest, setDigest] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [createdKey, setCreatedKey] = useState(null); // raw key, shown once
  const [referrals, setReferrals] = useState(null);
  const [hc, setHc] = useState(() => localStorage.getItem("rb_hc") === "1");
  const [storyModal, setStoryModal] = useState(false);
  const [storySituation, setStorySituation] = useState("");
  const [storyDraft, setStoryDraft] = useState(null); // {title, pages} awaiting save
  const [storyBusy, setStoryBusy] = useState(false);
  const [voiceName, setVoiceName] = useState(localStorage.getItem("rb_voice_name") || "");
  const [voicePitch, setVoicePitch] = useState(parseFloat(localStorage.getItem("rb_voice_pitch") || "1.0"));
  const [voices, setVoices] = useState([]);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [editingProfile, setEditingProfile] = useState(null); // profile id when renaming
  const [pinModal, setPinModal] = useState(null); // null | "set" | "change" | "remove"
  const [profileName, setProfileName] = useState("");
  const [profileColor, setProfileColor] = useState("teal");
  const [loading, setLoading] = useState(true);

  const loadContent = useCallback(async (pid) => {
    const params = pid ? { profile_id: pid } : {};
    const [r, b, c, s] = await Promise.all([
      api.get("/routines", { params }),
      api.get("/boards", { params }),
      api.get("/choices", { params }),
      api.get("/progress/summary"),
    ]);
    setRoutines(r.data);
    setBoards(b.data);
    setChoices(c.data);
    setSummary(s.data);
    if (pid) {
      try {
        const [ftRes, moodRes, stRes] = await Promise.all([
          api.get("/firstthen", { params: { profile_id: pid } }),
          api.get("/mood", { params: { profile_id: pid, days: 1 } }),
          api.get("/stories", { params: { profile_id: pid } }),
        ]);
        if (ftRes.data.first_label) setFt({ first_label: ftRes.data.first_label, first_icon: ftRes.data.first_icon, then_label: ftRes.data.then_label, then_icon: ftRes.data.then_icon });
        setTodayMood(moodRes.data.moods[0]?.mood || null);
        setStories(stRes.data);
      } catch { /* optional extras */ }
    }
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/profiles");
        setProfiles(data);
        const stored = localStorage.getItem("rb_profile");
        const pid = data.find((p) => p.id === stored)?.id || data[0]?.id || "";
        setActiveProfile(pid);
        if (pid) localStorage.setItem("rb_profile", pid);
        await loadContent(pid);
        if (user?.org_id) {
          const { data: orgData } = await api.get("/org");
          setOrg(orgData);
          if (orgData.my_role === "owner") {
            const { data: keys } = await api.get("/org/api-keys");
            setApiKeys(keys);
          }
        }
        const { data: tpl } = await api.get("/templates");
        setTemplates(tpl);
        const { data: dg } = await api.get("/care-circle/digest");
        setDigest(dg.profiles || []);
        const { data: ref } = await api.get("/referrals");
        setReferrals(ref);
        const loadVoices = () => setVoices(listVoices().filter((v) => v.lang.startsWith("en")));
        loadVoices();
        window.speechSynthesis?.addEventListener?.("voiceschanged", loadVoices);
      } catch (e) {
        toast.error(apiError(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [loadContent, user?.org_id]);

  const inviteStaff = async () => {
    try {
      const { data } = await api.post("/org/invites");
      setLastInvite(data.code);
    } catch (e) { toast.error(apiError(e)); }
  };

  const copyInviteLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/login?join=${lastInvite}`);
      toast.success("Invite link copied — send it to your staff member");
    } catch {
      toast.error("Could not copy — long-press the code to copy it");
    }
  };

  const removeMember = async (id) => {
    try {
      await api.delete(`/org/members/${id}`);
      setOrg((o) => ({ ...o, members: o.members.filter((m) => m.id !== id) }));
      toast.success("Staff member removed");
    } catch (e) { toast.error(apiError(e)); }
  };

  const switchProfile = async (pid) => {
    setActiveProfile(pid);
    localStorage.setItem("rb_profile", pid);
    await loadContent(pid);
  };

  const openAddProfile = () => {
    setEditingProfile(null);
    setProfileName("");
    setProfileColor("teal");
    setShowProfileModal(true);
  };

  const openEditProfile = (p) => {
    setEditingProfile(p.id);
    setProfileName(p.name);
    setProfileColor(p.color || "teal");
    setShowProfileModal(true);
  };

  const saveProfile = async () => {
    try {
      if (editingProfile) {
        const { data } = await api.patch(`/profiles/${editingProfile}`, { name: profileName, color: profileColor });
        setProfiles((ps) => ps.map((p) => (p.id === editingProfile ? data : p)));
        toast.success(`Renamed to "${data.name}"`);
      } else {
        const { data } = await api.post("/profiles", { name: profileName, color: profileColor });
        setProfiles((p) => [...p, data]);
        await switchProfile(data.id);
        toast.success(`Profile "${data.name}" created`);
      }
      setShowProfileModal(false);
      setProfileName("");
      setEditingProfile(null);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const deleteRoutine = async (id) => {
    try {
      await api.delete(`/routines/${id}`);
      setRoutines((r) => r.filter((x) => x.id !== id));
      toast.success("Routine deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteBoard = async (id) => {
    try {
      await api.delete(`/boards/${id}`);
      setBoards((b) => b.filter((x) => x.id !== id));
      toast.success("Board deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteChoice = async (id) => {
    try {
      await api.delete(`/choices/${id}`);
      setChoices((c) => c.filter((x) => x.id !== id));
      toast.success("Choice board deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const shareRoutine = async (id) => {
    try {
      const { data } = await api.post(`/routines/${id}/share`);
      const url = `${window.location.origin}/shared/${data.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — anyone with it can view this routine");
    } catch (e) { toast.error(apiError(e)); }
  };

  const shareBoard = async (id) => {
    try {
      const { data } = await api.post(`/boards/${id}/share`);
      const url = `${window.location.origin}/shared-board/${data.share_token}`;
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied — anyone with it can view this board");
    } catch (e) { toast.error(apiError(e)); }
  };

  const deleteAccount = async () => {
    try {
      await api.delete("/account", { data: { password: deletePassword } });
      toast.success("Your account and data have been deleted");
      window.location.href = "/";
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const updateVoiceRate = (v) => {
    setVoiceRate(v);
    localStorage.setItem("rb_voice_rate", String(v));
  };

  const updateVoiceName = (v) => {
    setVoiceName(v);
    if (v) localStorage.setItem("rb_voice_name", v);
    else localStorage.removeItem("rb_voice_name");
  };

  const updateVoicePitch = (v) => {
    setVoicePitch(v);
    localStorage.setItem("rb_voice_pitch", String(v));
  };

  const installTemplate = async (tpl) => {
    try {
      const { data } = await api.post("/routines", {
        profile_id: activeProfile,
        name: tpl.name,
        icon: tpl.icon,
        steps: tpl.steps,
      });
      setRoutines((r) => [...r, data]);
      toast.success(`"${tpl.name}" added — tap edit to customise it`);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const saveFirstThen = async () => {
    if (!ft.first_label.trim() || !ft.then_label.trim()) return toast.error("Fill in both the First and Then activities");
    try {
      await api.put("/firstthen", { profile_id: activeProfile, ...ft });
      toast.success("First–Then board saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const generateStory = async () => {
    if (!storySituation.trim()) return toast.error("Describe the situation first");
    setStoryBusy(true);
    try {
      const { data } = await api.post("/ai/social-story", {
        situation: storySituation.trim(),
        person_name: profiles.find((p) => p.id === activeProfile)?.name || "",
      });
      setStoryDraft(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setStoryBusy(false);
    }
  };

  const saveStory = async () => {
    setStoryBusy(true);
    try {
      await api.post("/stories", { profile_id: activeProfile, title: storyDraft.title, pages: storyDraft.pages });
      toast.success(`Story "${storyDraft.title}" saved`);
      setStoryModal(false);
      setStoryDraft(null);
      setStorySituation("");
      const { data } = await api.get("/stories", { params: { profile_id: activeProfile } });
      setStories(data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setStoryBusy(false);
    }
  };

  const deleteStory = async (id) => {
    try {
      await api.delete(`/stories/${id}`);
      setStories((s) => s.filter((x) => x.id !== id));
      toast.success("Story deleted");
    } catch (e) { toast.error(apiError(e)); }
  };

  const publishToGallery = async (kind, itemId) => {
    try {
      await api.post("/gallery/publish", { kind, item_id: itemId });
      toast.success("Published to the community gallery — thank you! (icons only, no photos)");
    } catch (e) { toast.error(apiError(e)); }
  };

  const MOOD_ICONS = { happy: Smile, okay: CloudRain, sad: Frown };
  const MoodIcon = todayMood ? MOOD_ICONS[todayMood] : null;

  const createBoard = async () => {
    try {
      const { data } = await api.post("/boards", { profile_id: activeProfile, name: "My Talk Board", seed: true });
      setBoards((b) => [...b, data]);
      navigate(`/app/boards/${data.id}`);
    } catch (e) { toast.error(apiError(e)); }
  };

  const [exporting, setExporting] = useState(null);
  const exportWord = async (p) => {
    if (!canUsePro) { navigate("/premium"); return; }
    setExporting(p.id);
    try {
      const res = await api.get(`/profiles/${p.id}/export.docx`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${p.name.replace(/[^A-Za-z0-9]+/g, "-")}-care-record.docx`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Care record for ${p.name} downloaded`);
    } catch (e) {
      toast.error(e.response?.data instanceof Blob ? "Word export is a Pro feature — upgrade for unlimited access." : apiError(e));
    } finally { setExporting(null); }
  };

  const currentProfile = profiles.find((p) => p.id === activeProfile);
  const atBoardLimit = !canUsePro && boards.length >= 1;

  return (
    <div data-testid="dashboard" className="min-h-screen bg-background">
      <Header />
      <main className="max-w-5xl mx-auto px-4 py-8 pb-24 space-y-10">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Hello, {user?.name}</h1>
            <p className="mt-1 text-lg text-muted-foreground">Carer dashboard — set up routines and boards here.</p>
          </div>
          {!isPremium && (
            <button
              data-testid="upgrade-banner"
              onClick={() => navigate("/premium")}
              className="min-h-[56px] px-6 rounded-full bg-[#ECC94B] font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
            >
              <Crown size={22} strokeWidth={2.5} /> Go Premium — £5/month
            </button>
          )}
        </div>

        <section>
          <div className="flex items-center gap-3 mb-4">
            <Users size={28} strokeWidth={2.5} className="text-primary" />
            <h2 className="text-2xl font-extrabold">Who are you supporting?</h2>
          </div>
          <div className="flex flex-wrap gap-3">
            {profiles.map((p) => (
              <div key={p.id} className="flex items-center gap-1">
                <button
                  data-testid={`profile-select-${p.id}`}
                  onClick={() => switchProfile(p.id)}
                  className={`min-h-[56px] px-5 rounded-full border-2 font-bold text-lg flex items-center gap-2 transition-colors duration-200 ${
                    p.id === activeProfile ? "border-primary bg-primary/10" : "border-foreground/20 bg-white hover:bg-secondary"
                  }`}
                >
                  <span className="w-4 h-4 rounded-full" style={{ background: COLOR_BG[p.color] || COLOR_BG.teal }} />
                  {p.name}
                  {p.shared && <span data-testid={`shared-badge-${p.id}`} className="text-xs font-bold px-2 py-1 rounded-full bg-[#4299E1]/15 text-[#2B6CB0]">Shared</span>}
                  {p.id === activeProfile && MoodIcon && <MoodIcon size={20} strokeWidth={2.5} className="text-muted-foreground" />}
                </button>
                {!p.shared && (
                <button
                  data-testid={`circle-profile-${p.id}`}
                  onClick={() => setCircleFor(p)}
                  aria-label={`Care circle for ${p.name}`}
                  title="Care circle — share with parents, teachers, therapists"
                  className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200"
                >
                  <Users size={18} strokeWidth={2.5} />
                </button>
                )}
                {!p.shared && (
                <button
                  data-testid={`status-profile-${p.id}`}
                  onClick={async () => {
                    try {
                      const { data } = await api.post(`/profiles/${p.id}/status-link`);
                      await navigator.clipboard.writeText(`${window.location.origin}/status/${data.status_token}`);
                      toast.success("Status link copied — no login needed to view it");
                    } catch (e) { toast.error(apiError(e)); }
                  }}
                  aria-label={`Copy status link for ${p.name}`}
                  title="Status link — 5-second live summary, no login needed"
                  className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200"
                >
                  <Check size={18} strokeWidth={2.5} />
                </button>
                )}
                <button
                  data-testid={`handover-profile-${p.id}`}
                  onClick={() => navigate(`/app/handover/${p.id}`)}
                  aria-label={`Handover report for ${p.name}`}
                  title="Handover report"
                  className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200"
                >
                  <Printer size={18} strokeWidth={2.5} />
                </button>
                <button
                  data-testid={`export-profile-${p.id}`}
                  onClick={() => exportWord(p)}
                  disabled={exporting === p.id}
                  aria-label={`Download Word care record for ${p.name}`}
                  title="Download care record (Word .docx)"
                  className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200 disabled:opacity-50"
                >
                  <Download size={18} strokeWidth={2.5} />
                </button>
                <button
                  data-testid={`notes-profile-${p.id}`}
                  onClick={() => navigate(`/app/notes/${p.id}`)}
                  aria-label={`Care notes for ${p.name}`}
                  title="Care notes"
                  className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200"
                >
                  <FileText size={18} strokeWidth={2.5} />
                </button>
                <button
                  data-testid={`care-plans-profile-${p.id}`}
                  onClick={() => navigate(`/app/care-plans/${p.id}`)}
                  aria-label={`Care plans for ${p.name}`}
                  title="Care plans — AI summaries & routine suggestions"
                  className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200"
                >
                  <FolderOpen size={18} strokeWidth={2.5} />
                </button>
                {!p.shared && (
                <button
                  data-testid={`rename-profile-${p.id}`}
                  onClick={() => openEditProfile(p)}
                  aria-label={`Rename ${p.name}`}
                  className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 bg-white grid place-items-center hover:bg-secondary transition-colors duration-200"
                >
                  <Pencil size={18} strokeWidth={2.5} />
                </button>
                )}
              </div>
            ))}
            <button
              data-testid="add-profile-button"
              onClick={() => (canUsePro || profiles.length < 1 ? openAddProfile() : navigate("/premium"))}
              className="min-h-[56px] px-5 rounded-full border-2 border-dashed border-foreground/30 bg-white font-bold text-lg flex items-center gap-2 transition-colors duration-200 hover:bg-secondary"
            >
              {!canUsePro && profiles.length >= 1 ? <Crown size={20} strokeWidth={2.5} /> : <Plus size={20} strokeWidth={2.5} />}
              Add person
            </button>
          </div>
        </section>

        {digest.length > 0 && (
          <section data-testid="circle-digest-section">
            <div className="flex items-center gap-3 mb-4">
              <Users size={28} strokeWidth={2.5} className="text-primary" />
              <h2 className="text-2xl font-extrabold">Today at a glance</h2>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {digest.map((d) => {
                const DmIcon = d.mood ? MOOD_ICONS[d.mood] : null;
                return (
                  <div key={d.profile_id} data-testid={`digest-card-${d.profile_id}`} className="bg-white rounded-3xl border-2 border-foreground/20 p-5">
                    <div className="flex items-center gap-2 mb-3">
                      <span className="w-4 h-4 rounded-full" style={{ background: COLOR_BG[d.color] || COLOR_BG.teal }} />
                      <p className="font-extrabold text-lg">{d.name}</p>
                      <span className="ml-auto flex items-center gap-1 text-sm font-semibold text-muted-foreground">
                        {DmIcon ? <DmIcon size={18} strokeWidth={2.5} /> : null}
                        {d.mood ? `feeling ${d.mood}` : "no mood logged"}
                      </span>
                    </div>
                    {d.routines.length === 0 ? (
                      <p className="text-sm font-semibold text-muted-foreground">No routines set up yet.</p>
                    ) : (
                      <ul className="space-y-2">
                        {d.routines.map((r) => (
                          <li key={r.name} className="flex items-center gap-2">
                            <IconRenderer name={r.icon} size={18} />
                            <span className="font-semibold">{r.name}</span>
                            <span className={`ml-auto text-sm font-bold ${r.total > 0 && r.done >= r.total ? "text-[#38A169]" : "text-muted-foreground"}`}>
                              {r.done}/{r.total} steps
                            </span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {org && (
          <section data-testid="team-section">
            {org.plan !== "business" && org.my_role === "owner" && (
              <div data-testid="org-upgrade-banner" className="mb-4 bg-[#4299E1]/10 border-2 border-[#4299E1]/40 rounded-3xl p-5 flex flex-wrap items-center justify-between gap-3">
                <p className="font-bold text-lg">Your organisation is on the free plan — limits apply.</p>
                <button onClick={() => navigate("/premium")} data-testid="org-upgrade-link" className="min-h-[52px] px-5 rounded-full bg-[#4299E1] text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5">
                  <Building2 size={20} strokeWidth={2.5} /> Organisation plan — £15/month
                </button>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                <Building2 size={28} strokeWidth={2.5} className="text-primary" />
                <h2 className="text-2xl font-extrabold">{org.name} — Team</h2>
              </div>
              {org.my_role === "owner" && (
                <button
                  data-testid="invite-staff-button"
                  onClick={inviteStaff}
                  className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                >
                  <Plus size={20} strokeWidth={3} /> Invite staff
                </button>
              )}
            </div>
            {lastInvite && (
              <div data-testid="invite-code-display" className="mb-4 bg-[#ECC94B]/20 border-2 border-[#ECC94B] rounded-3xl p-5 flex flex-wrap items-center gap-3">
                <p className="font-bold text-lg">Invite code: <span className="font-extrabold tracking-widest">{lastInvite}</span></p>
                <button data-testid="copy-invite-link-button" onClick={copyInviteLink} className="min-h-[48px] px-4 rounded-full bg-white border-2 border-foreground/25 font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
                  <Copy size={18} strokeWidth={2.5} /> Copy invite link
                </button>
              </div>
            )}
            <div className="bg-white rounded-3xl border-2 border-foreground/10 p-5 space-y-3">
              {org.members.map((m) => (
                <div key={m.id} data-testid={`member-row-${m.id}`} className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-full bg-primary/10 grid place-items-center font-extrabold text-primary">{m.name?.[0]?.toUpperCase()}</span>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold truncate">{m.name} {m.id === user?.id ? "(you)" : ""}</p>
                    <p className="text-sm font-semibold text-muted-foreground truncate">{m.email} · {m.org_role}</p>
                  </div>
                  {org.my_role === "owner" && m.id !== user?.id && (
                    <button data-testid={`remove-member-${m.id}`} onClick={() => removeMember(m.id)} aria-label={`Remove ${m.name}`} className="min-w-[44px] min-h-[44px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200">
                      <Trash2 size={18} strokeWidth={2.5} />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {org.my_role === "owner" && (
              <div data-testid="api-access-section" className="mt-6 bg-white rounded-3xl border-2 border-foreground/10 p-5 space-y-3">
                <p className="text-lg font-bold flex items-center gap-2"><KeyRound size={22} strokeWidth={2.5} className="text-primary" /> API access</p>
                <p className="text-sm font-semibold text-muted-foreground">Let care-management software read your residents' routines, moods and notes. Keys are read-only and shown once.</p>
                {createdKey && (
                  <div data-testid="created-api-key" className="bg-[#ECC94B]/20 border-2 border-[#ECC94B] rounded-2xl p-4 space-y-2">
                    <p className="font-bold">Copy this key now — it will never be shown again:</p>
                    <code className="block text-sm font-mono break-all bg-white rounded-xl p-3 border-2 border-foreground/15">{createdKey}</code>
                    <button data-testid="copy-api-key-button" onClick={async () => { try { await navigator.clipboard.writeText(createdKey); toast.success("Key copied"); } catch { toast.error("Could not copy — select the key text manually"); } }} className="min-h-[48px] px-4 rounded-full bg-white border-2 border-foreground/25 font-bold flex items-center gap-2 hover:bg-secondary transition-colors duration-200">
                      <Copy size={18} strokeWidth={2.5} /> Copy key
                    </button>
                  </div>
                )}
                <div className="flex flex-wrap gap-2">
                  <input
                    data-testid="api-key-name-input"
                    value={newKeyName}
                    onChange={(e) => setNewKeyName(e.target.value)}
                    placeholder="Key name, e.g. Nourish export"
                    className="min-h-[56px] flex-1 min-w-[200px] rounded-full border-2 border-foreground/25 px-5 font-semibold bg-white"
                  />
                  <button
                    data-testid="create-api-key-button"
                    disabled={!newKeyName.trim()}
                    onClick={async () => {
                      try {
                        const { data } = await api.post("/org/api-keys", { name: newKeyName.trim() });
                        setCreatedKey(data.key);
                        setNewKeyName("");
                        const { data: keys } = await api.get("/org/api-keys");
                        setApiKeys(keys);
                      } catch (e) { toast.error(apiError(e)); }
                    }}
                    className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5 disabled:opacity-50"
                  >
                    <Plus size={20} strokeWidth={3} /> Create key
                  </button>
                </div>
                {apiKeys.length > 0 && (
                  <ul className="space-y-2">
                    {apiKeys.map((k) => (
                      <li key={k.key_id} data-testid={`api-key-row-${k.key_id}`} className="flex items-center gap-3 rounded-2xl border-2 border-foreground/10 p-3">
                        <div className="flex-1 min-w-0">
                          <p className="font-bold truncate">{k.name}</p>
                          <p className="text-sm font-semibold text-muted-foreground truncate">{k.prefix} · created {new Date(k.created_at).toLocaleDateString()}{k.last_used_at ? ` · last used ${new Date(k.last_used_at).toLocaleDateString()}` : " · never used"}</p>
                        </div>
                        <button
                          data-testid={`revoke-api-key-${k.key_id}`}
                          onClick={async () => {
                            try {
                              await api.delete(`/org/api-keys/${k.key_id}`);
                              setApiKeys((ks) => ks.filter((x) => x.key_id !== k.key_id));
                              toast.success("Key revoked");
                            } catch (e) { toast.error(apiError(e)); }
                          }}
                          aria-label={`Revoke ${k.name}`}
                          className="min-w-[44px] min-h-[44px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200"
                        >
                          <Trash2 size={18} strokeWidth={2.5} />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </section>
        )}

        {loading ? (
          <p className="text-lg text-muted-foreground">Loading…</p>
        ) : (
          <>
            <section>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <ListChecks size={28} strokeWidth={2.5} className="text-primary" />
                  <h2 className="text-2xl font-extrabold">Visual Routines</h2>
                </div>
                <div className="flex items-center gap-4">
                  <button
                    data-testid="new-routine-button"
                    onClick={() => navigate("/app/routines/new")}
                    className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                  >
                    <Plus size={20} strokeWidth={3} />
                    New routine
                  </button>
                </div>
              </div>
              {routines.length === 0 ? (
                <p data-testid="no-routines-message" className="bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
                  No routines yet for {currentProfile?.name || "this person"}. Create your first one — e.g. "Morning routine".
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {routines.map((r) => (
                    <div key={r.id} data-testid={`routine-card-${r.id}`} className="bg-white rounded-3xl border-2 border-foreground/10 p-5 flex items-center gap-4">
                      <span className="w-16 h-16 rounded-2xl bg-primary/10 grid place-items-center shrink-0">
                        <IconRenderer name={r.icon} size={32} className="text-primary" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-extrabold truncate">{r.name}</h3>
                        <p className="text-muted-foreground font-semibold">{r.steps.length} steps</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button data-testid={`publish-routine-${r.id}`} onClick={() => publishToGallery("routine", r.id)} aria-label={`Publish ${r.name} to community gallery`} title="Publish to community gallery" className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200">
                          <Globe size={20} strokeWidth={2.5} />
                        </button>
                        <button data-testid={`share-routine-${r.id}`} onClick={() => shareRoutine(r.id)} aria-label={`Share ${r.name}`} className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200">
                          <Link2 size={20} strokeWidth={2.5} />
                        </button>
                        <button data-testid={`edit-routine-${r.id}`} onClick={() => navigate(`/app/routines/${r.id}/edit`)} aria-label={`Edit ${r.name}`} className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200">
                          <Pencil size={20} strokeWidth={2.5} />
                        </button>
                        <button data-testid={`delete-routine-${r.id}`} onClick={() => deleteRoutine(r.id)} aria-label={`Delete ${r.name}`} className="min-w-[48px] min-h-[48px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200">
                          <Trash2 size={20} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section data-testid="templates-section">
              <div className="flex items-center gap-3 mb-4">
                <Sparkles size={28} strokeWidth={2.5} className="text-[#B7791F]" />
                <h2 className="text-2xl font-extrabold">Start from a template</h2>
              </div>
              <div className="flex flex-wrap gap-3">
                {templates.map((t) => (
                  <button
                    key={t.name}
                    data-testid={`template-${t.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    onClick={() => installTemplate(t)}
                    className="min-h-[56px] px-5 rounded-full border-2 border-foreground/20 bg-white font-bold text-base flex items-center gap-2 hover:bg-secondary transition-colors duration-200"
                  >
                    <IconRenderer name={t.icon} size={22} className="text-primary" />
                    {t.name}
                  </button>
                ))}
              </div>
              <p className="mt-2 text-sm text-muted-foreground font-semibold">One tap adds a ready-made routine you can then edit. Free tier: 3 routines total.</p>
            </section>

            <section>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <LayoutGrid size={28} strokeWidth={2.5} className="text-[#9F7AEA]" />
                  <h2 className="text-2xl font-extrabold">Communication Boards</h2>
                </div>
                <div className="flex items-center gap-4">
                  {!canUsePro && <LimitBar used={boards.length} max={1} label="Free boards" testId="board-limit-indicator" />}
                  <button
                    data-testid="new-board-button"
                    onClick={() => (atBoardLimit ? navigate("/premium") : createBoard())}
                    className="min-h-[56px] px-6 rounded-full bg-[#9F7AEA] text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                  >
                    {atBoardLimit ? <Crown size={20} strokeWidth={2.5} /> : <Plus size={20} strokeWidth={3} />}
                    New board
                  </button>
                </div>
              </div>
              {boards.length === 0 ? (
                <p data-testid="no-boards-message" className="bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
                  No board yet. Create one and it comes pre-filled with everyday symbols you can edit.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {boards.map((b) => (
                    <div key={b.id} data-testid={`board-card-${b.id}`} className="bg-white rounded-3xl border-2 border-foreground/10 p-5 flex items-center gap-4">
                      <span className="w-16 h-16 rounded-2xl bg-[#9F7AEA]/10 grid place-items-center shrink-0">
                        <LayoutGrid size={32} strokeWidth={2.5} className="text-[#9F7AEA]" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-extrabold truncate">{b.name}</h3>
                        <p className="text-muted-foreground font-semibold">{b.tiles.length} tiles{!canUsePro ? ` of 20` : ""}</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button data-testid={`publish-board-${b.id}`} onClick={() => publishToGallery("board", b.id)} aria-label={`Publish ${b.name} to community gallery`} title="Publish to community gallery" className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200">
                          <Globe size={20} strokeWidth={2.5} />
                        </button>
                        <button data-testid={`share-board-${b.id}`} onClick={() => shareBoard(b.id)} aria-label={`Share ${b.name}`} className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200">
                          <Link2 size={20} strokeWidth={2.5} />
                        </button>
                        <button data-testid={`edit-board-${b.id}`} onClick={() => navigate(`/app/boards/${b.id}`)} aria-label={`Edit ${b.name}`} className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200">
                          <Pencil size={20} strokeWidth={2.5} />
                        </button>
                        <button data-testid={`delete-board-${b.id}`} onClick={() => deleteBoard(b.id)} aria-label={`Delete ${b.name}`} className="min-w-[48px] min-h-[48px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200">
                          <Trash2 size={20} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section>
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <ClipboardList size={28} strokeWidth={2.5} className="text-[#ED8936]" />
                  <h2 className="text-2xl font-extrabold">Choice Boards</h2>
                </div>
                <div className="flex items-center gap-4">
                  {!canUsePro && <LimitBar used={choices.length} max={3} label="Free choice boards" testId="choice-limit-indicator" />}
                  <button
                    data-testid="new-choice-button"
                    onClick={() => (!canUsePro && choices.length >= 3 ? navigate("/premium") : navigate("/app/choices/new"))}
                    className="min-h-[56px] px-6 rounded-full bg-[#ED8936] text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                  >
                    {!canUsePro && choices.length >= 3 ? <Crown size={20} strokeWidth={2.5} /> : <Plus size={20} strokeWidth={3} />}
                    New choice board
                  </button>
                </div>
              </div>
              {choices.length === 0 ? (
                <p data-testid="no-choices-message" className="bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-8 text-lg text-muted-foreground">
                  No choice boards yet. Create one like "What snack would you like?" — the person taps a picture to choose.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {choices.map((c) => (
                    <div key={c.id} data-testid={`choice-card-${c.id}`} className="bg-white rounded-3xl border-2 border-foreground/10 p-5 flex items-center gap-4">
                      <span className="w-16 h-16 rounded-2xl bg-[#ED8936]/10 grid place-items-center shrink-0">
                        <ClipboardList size={32} strokeWidth={2.5} className="text-[#ED8936]" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-extrabold truncate">{c.name}</h3>
                        <p className="text-muted-foreground font-semibold">{c.options.length} options</p>
                      </div>
                      <div className="flex flex-col gap-2">
                        <button data-testid={`edit-choice-${c.id}`} onClick={() => navigate(`/app/choices/${c.id}/edit`)} aria-label={`Edit ${c.name}`} className="min-w-[48px] min-h-[48px] rounded-full border-2 border-foreground/20 grid place-items-center hover:bg-secondary transition-colors duration-200">
                          <Pencil size={20} strokeWidth={2.5} />
                        </button>
                        <button data-testid={`delete-choice-${c.id}`} onClick={() => deleteChoice(c.id)} aria-label={`Delete ${c.name}`} className="min-w-[48px] min-h-[48px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200">
                          <Trash2 size={20} strokeWidth={2.5} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section data-testid="stories-section">
              <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex items-center gap-3">
                  <BookOpen size={28} strokeWidth={2.5} className="text-[#9F7AEA]" />
                  <h2 className="text-2xl font-extrabold">Social stories</h2>
                </div>
                {canUsePro ? (
                  <button
                    data-testid="new-story-button"
                    onClick={() => setStoryModal(true)}
                    className="min-h-[56px] px-6 rounded-full bg-[#9F7AEA] text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                  >
                    <Sparkles size={20} strokeWidth={2.5} /> New story with AI
                  </button>
                ) : (
                  <button
                    data-testid="new-story-locked-button"
                    onClick={() => navigate("/premium")}
                    className="min-h-[56px] px-6 rounded-full border-2 border-dashed border-foreground/25 bg-secondary/50 font-bold flex items-center gap-2 text-muted-foreground"
                  >
                    <Lock size={20} strokeWidth={2.5} /> New story with AI (Pro)
                  </button>
                )}
              </div>
              <p className="mb-4 text-muted-foreground font-semibold">Personalised stories that prepare someone for new situations — "Going to the dentist", "A new carer starts". Read together in User Mode.</p>
              {stories.length === 0 ? (
                <p data-testid="no-stories-dashboard" className="bg-white rounded-3xl border-2 border-dashed border-foreground/20 p-6 text-lg text-muted-foreground">
                  No stories yet for this person.
                </p>
              ) : (
                <div className="grid sm:grid-cols-2 gap-4">
                  {stories.map((s) => (
                    <div key={s.id} data-testid={`story-card-${s.id}`} className="bg-white rounded-3xl border-2 border-foreground/10 p-5 flex items-center gap-4">
                      <span className="w-14 h-14 rounded-2xl bg-[#9F7AEA]/10 grid place-items-center shrink-0">
                        <BookOpen size={28} strokeWidth={2.5} className="text-[#9F7AEA]" />
                      </span>
                      <div className="flex-1 min-w-0">
                        <h3 className="text-xl font-extrabold truncate">{s.title}</h3>
                        <p className="text-muted-foreground font-semibold">{s.pages.length} pages</p>
                      </div>
                      <button data-testid={`delete-story-${s.id}`} onClick={() => deleteStory(s.id)} aria-label={`Delete ${s.title}`} className="min-w-[48px] min-h-[48px] rounded-full border-2 border-[#F56565]/40 text-[#C53030] grid place-items-center hover:bg-[#F56565]/10 transition-colors duration-200 shrink-0">
                        <Trash2 size={20} strokeWidth={2.5} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </section>

            <section className="bg-white rounded-3xl border-2 border-foreground/10 p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-lg font-bold flex items-center gap-2"><Globe size={22} strokeWidth={2.5} className="text-primary" /> Community gallery</p>
                <p className="text-muted-foreground font-semibold">Browse routines and boards shared by other carers. Publish yours with the globe button on any card.</p>
              </div>
              <button
                data-testid="browse-gallery-button"
                onClick={() => navigate("/app/gallery")}
                className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold transition-transform duration-200 ease-out hover:-translate-y-0.5"
              >
                Browse gallery
              </button>
            </section>

            <section data-testid="first-then-section">
              <div className="flex items-center gap-3 mb-4">
                <ArrowRight size={28} strokeWidth={2.5} className="text-[#4299E1]" />
                <h2 className="text-2xl font-extrabold">First–Then board</h2>
              </div>
              <div className="bg-white rounded-3xl border-2 border-foreground/10 p-6">
                <p className="text-muted-foreground font-semibold mb-4">"First we do this, then we do that" — a classic for transitions.</p>
                <div className="grid sm:grid-cols-2 gap-4">
                  {["first", "then"].map((slot) => (
                    <div key={slot} className="border-2 border-foreground/10 rounded-2xl p-4">
                      <p className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-2">{slot === "first" ? "First (task)" : "Then (reward)"}</p>
                      <div className="flex items-center gap-3">
                        <button
                          data-testid={`ft-${slot}-icon-button`}
                          onClick={() => setFtPickerFor(slot)}
                          aria-label={`Choose ${slot} icon`}
                          className="w-16 h-16 rounded-2xl border-2 border-foreground/15 grid place-items-center shrink-0 hover:bg-secondary transition-colors duration-200"
                        >
                          <IconRenderer name={ft[`${slot}_icon`]} size={32} className="text-[#4299E1]" />
                        </button>
                        <input
                          data-testid={`ft-${slot}-label-input`}
                          value={ft[`${slot}_label`]}
                          onChange={(e) => setFt((f) => ({ ...f, [`${slot}_label`]: e.target.value }))}
                          placeholder={slot === "first" ? "e.g. Brush teeth" : "e.g. Watch TV"}
                          className="flex-1 min-w-0 min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg focus:outline-none focus:border-primary"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <button
                  data-testid="ft-save-button"
                  onClick={saveFirstThen}
                  className="mt-4 min-h-[56px] px-6 rounded-full bg-[#4299E1] text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                >
                  <Check size={22} strokeWidth={3} /> Save First–Then board
                </button>
              </div>
            </section>

            {summary && summary.routines.length > 0 && (
              <section data-testid="progress-summary">
                <div className="flex items-center gap-3 mb-4">
                  <Check size={28} strokeWidth={3} className="text-[#2F855A]" />
                  <h2 className="text-2xl font-extrabold">This week</h2>
                </div>
                <div className="bg-white rounded-3xl border-2 border-foreground/10 p-5 space-y-4">
                  {summary.routines.map((r) => (
                    <div key={r.id} data-testid={`progress-row-${r.id}`} className="flex items-center gap-3">
                      <IconRenderer name={r.icon} size={24} className="text-primary shrink-0" />
                      <span className="font-bold text-base flex-1 truncate">{r.name}</span>
                      <div className="flex gap-1 shrink-0">
                        {r.days.map((d) => (
                          <span
                            key={d.date}
                            title={`${d.date}: ${d.done}/${r.total} steps`}
                            className={`w-6 h-6 rounded-md ${
                              r.total > 0 && d.done >= r.total ? "bg-[#48BB78]" : d.done > 0 ? "bg-[#ECC94B]" : "bg-secondary border-2 border-foreground/10"
                            }`}
                          />
                        ))}
                      </div>
                    </div>
                  ))}
                  <p className="text-sm text-muted-foreground font-semibold">Green = all steps done, yellow = some done, grey = none. Newest day on the right.</p>
                </div>
              </section>
            )}

            <section className="bg-white rounded-3xl border-2 border-foreground/10 p-6">
              <p className="text-lg font-bold flex items-center gap-2"><Volume2 size={22} strokeWidth={2.5} className="text-primary" /> Voice settings</p>
              <p className="text-muted-foreground font-semibold">How the app sounds when it speaks aloud.</p>
              <div className="mt-4 space-y-4">
                <div>
                  <label htmlFor="voice-select" className="block text-base font-bold mb-1">Voice</label>
                  <select
                    id="voice-select"
                    data-testid="voice-select"
                    value={voiceName}
                    onChange={(e) => updateVoiceName(e.target.value)}
                    className="w-full min-h-[56px] rounded-2xl border-2 border-foreground/25 bg-white px-4 text-lg focus:outline-none focus:border-primary"
                  >
                    <option value="">Device default</option>
                    {voices.map((v) => (
                      <option key={v.name} value={v.name}>{v.name} ({v.lang})</option>
                    ))}
                  </select>
                  {voices.length === 0 && (
                    <p className="mt-1 text-sm font-semibold text-muted-foreground">No extra voices found on this device — the built-in default voice will be used.</p>
                  )}
                </div>
                <div>
                  <label className="block text-base font-bold mb-1">Speed — <span data-testid="voice-rate-value" className="text-muted-foreground">{voiceRate.toFixed(1)}×</span></label>
                  <input type="range" min="0.5" max="1.4" step="0.1" value={voiceRate} onChange={(e) => updateVoiceRate(parseFloat(e.target.value))} data-testid="voice-rate-slider" className="w-full accent-[#2C7A7B]" />
                </div>
                <div>
                  <label className="block text-base font-bold mb-1">Pitch — <span data-testid="voice-pitch-value" className="text-muted-foreground">{voicePitch.toFixed(1)}</span></label>
                  <input type="range" min="0.5" max="1.8" step="0.1" value={voicePitch} onChange={(e) => updateVoicePitch(parseFloat(e.target.value))} data-testid="voice-pitch-slider" className="w-full accent-[#2C7A7B]" />
                </div>
                <button
                  data-testid="voice-preview-button"
                  onClick={() => speak("Hello! This is how I will sound.")}
                  className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                >
                  <Volume2 size={22} strokeWidth={2.5} /> Preview voice
                </button>
              </div>
            </section>

            <section className="bg-white rounded-3xl border-2 border-foreground/10 p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-lg font-bold flex items-center gap-2"><Lock size={22} strokeWidth={2.5} className="text-primary" /> Carer PIN lock</p>
                <p className="text-muted-foreground font-semibold">
                  {user?.has_pin ? "Carer Mode is protected by your PIN." : "Set a 4-digit PIN so clients can't open Carer Mode and change things."}
                </p>
              </div>
              {user?.has_pin ? (
                <div className="flex gap-2">
                  <button data-testid="change-pin-button" onClick={() => setPinModal("change")} className="min-h-[56px] px-5 rounded-full border-2 border-foreground/25 bg-white font-bold hover:bg-secondary transition-colors duration-200">
                    Change PIN
                  </button>
                  <button data-testid="remove-pin-button" onClick={() => setPinModal("remove")} className="min-h-[56px] px-5 rounded-full border-2 border-[#F56565]/40 text-[#C53030] font-bold hover:bg-[#F56565]/10 transition-colors duration-200">
                    Remove
                  </button>
                </div>
              ) : (
                <button data-testid="set-pin-button" onClick={() => setPinModal("set")} className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold transition-transform duration-200 ease-out hover:-translate-y-0.5">
                  Set a PIN
                </button>
              )}
            </section>

            {user?.role === "admin" && <AdminGrantCard />}

            {referrals && (
              <section data-testid="invite-section" className="bg-white rounded-3xl border-2 border-foreground/10 p-6 space-y-4">
                <div>
                  <p className="text-lg font-bold flex items-center gap-2"><Gift size={22} strokeWidth={2.5} className="text-primary" /> Invite friends, get a month free</p>
                  <p className="text-muted-foreground font-semibold">Share your link. When a friend subscribes, you get a free month — every time.</p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <code data-testid="referral-link" className="flex-1 min-w-[220px] text-sm font-mono bg-secondary/60 rounded-xl px-4 py-3 truncate">
                    {window.location.origin}/login?ref={referrals.code}
                  </code>
                  <button
                    data-testid="copy-referral-button"
                    onClick={async () => { try { await navigator.clipboard.writeText(`${window.location.origin}/login?ref=${referrals.code}`); toast.success("Invite link copied"); } catch { toast.error("Could not copy — long-press the link to copy it"); } }}
                    className="min-h-[48px] px-5 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
                  >
                    <Copy size={18} strokeWidth={2.5} /> Copy link
                  </button>
                </div>
                {referrals.rewards.length > 0 && (
                  <ul className="space-y-1">
                    {referrals.rewards.map((r, i) => (
                      <li key={i} className="text-sm font-semibold text-[#2F855A]">
                        {r.referred_name} subscribed — {r.reward === "coupon" ? "free month added to your subscription" : "30 free days added"} · {new Date(r.created_at).toLocaleDateString()}
                      </li>
                    ))}
                  </ul>
                )}
                {referrals.premium_until && new Date(referrals.premium_until) > new Date() && (
                  <p data-testid="premium-credit-note" className="text-sm font-bold text-[#2F855A]">Free Premium time active until {new Date(referrals.premium_until).toLocaleDateString()}</p>
                )}
              </section>
            )}

            <section className="bg-white rounded-3xl border-2 border-foreground/10 p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-lg font-bold flex items-center gap-2"><Eye size={22} strokeWidth={2.5} className="text-primary" /> High contrast & large text</p>
                <p className="text-muted-foreground font-semibold">For low-vision users: black background, vivid colours, bigger text. Applies on this device.</p>
              </div>
              <button
                data-testid="hc-toggle"
                aria-pressed={hc}
                onClick={() => {
                  const next = !hc;
                  setHc(next);
                  localStorage.setItem("rb_hc", next ? "1" : "0");
                  document.body.classList.toggle("hc", next);
                  toast.success(next ? "High contrast on" : "High contrast off");
                }}
                className={`min-h-[56px] px-6 rounded-full font-bold transition-colors duration-200 ${hc ? "bg-primary text-white" : "border-2 border-foreground/25 bg-white"}`}
              >
                {hc ? "On" : "Off"}
              </button>
            </section>

            <section className="bg-white rounded-3xl border-2 border-foreground/10 p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-lg font-bold flex items-center gap-2"><Mail size={22} strokeWidth={2.5} className="text-primary" /> Daily digest email</p>                <p className="text-muted-foreground font-semibold">A short morning email with yesterday's mood and routine progress for people in your Care Circle.</p>
              </div>
              <button
                data-testid="digest-toggle"
                onClick={async () => {
                  try {
                    await api.post("/auth/digest-pref", { enabled: user?.digest_email === false });
                    await refreshUser();
                    toast.success(user?.digest_email === false ? "Daily digest on" : "Daily digest off");
                  } catch (e) { toast.error(apiError(e)); }
                }}
                className={`min-h-[56px] px-6 rounded-full font-bold transition-colors duration-200 ${user?.digest_email !== false ? "bg-primary text-white" : "border-2 border-foreground/25 bg-white"}`}
              >
                {user?.digest_email !== false ? "On" : "Off"}
              </button>
            </section>

            <section className="bg-white rounded-3xl border-2 border-foreground/10 p-6 flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-lg font-bold">Your data</p>
                <p className="text-muted-foreground font-semibold">
                  Deleting your account permanently removes all profiles, routines, boards and notes.
                  {user?.org_role === "owner" ? " As the organisation owner, remove all staff first — this closes the organisation." : ""}
                </p>
              </div>
              <button data-testid="delete-account-button" onClick={() => setShowDeleteModal(true)} className="min-h-[56px] px-6 rounded-full border-2 border-[#F56565]/50 text-[#C53030] font-bold hover:bg-[#F56565]/10 transition-colors duration-200">
                Delete my account
              </button>
            </section>

            <section className="bg-primary/10 rounded-3xl border-2 border-primary/30 p-6 flex flex-wrap items-center justify-between gap-4">
              <p className="text-lg font-bold">Ready for {currentProfile?.name || "them"} to use the app?</p>
              <button
                data-testid="open-user-mode-button"
                onClick={() => navigate("/use")}
                className="min-h-[56px] px-6 rounded-full bg-primary text-white font-bold flex items-center gap-2 transition-transform duration-200 ease-out hover:-translate-y-0.5"
              >
                Open User Mode <ArrowRight size={20} strokeWidth={3} />
              </button>
            </section>
          </>
        )}
      </main>

      {storyModal && (
        <Modal title="New social story" onClose={() => { setStoryModal(false); setStoryDraft(null); }} testId="story-modal">
          {!storyDraft ? (
            <>
              <label htmlFor="story-situation" className="block text-base font-bold mb-1">What's the situation?</label>
              <textarea
                id="story-situation"
                data-testid="story-situation-input"
                value={storySituation}
                onChange={(e) => setStorySituation(e.target.value)}
                rows={4}
                placeholder={'e.g. "Going to the dentist for the first time" or "A new support worker starts on Monday"'}
                className="w-full rounded-2xl border-2 border-foreground/25 px-4 py-3 text-lg focus:outline-none focus:border-primary"
              />
              <p className="mt-2 text-sm text-muted-foreground font-semibold">AI writes a short, reassuring first-person story using their name. You can read it together in User Mode.</p>
              <button
                data-testid="story-generate-button"
                onClick={generateStory}
                disabled={storyBusy}
                className="mt-5 w-full min-h-[56px] rounded-full bg-[#9F7AEA] text-white text-lg font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              >
                <Sparkles size={22} strokeWidth={2.5} /> {storyBusy ? "Writing…" : "Write the story"}
              </button>
            </>
          ) : (
            <>
              <h3 data-testid="story-draft-title" className="text-xl font-extrabold">{storyDraft.title}</h3>
              <div className="mt-4 space-y-3 max-h-72 overflow-y-auto">
                {storyDraft.pages.map((p, i) => (
                  <div key={i} data-testid={`story-draft-page-${i}`} className="flex items-center gap-3 border-2 border-foreground/10 rounded-2xl p-3">
                    <IconRenderer name={p.icon} size={28} className="text-[#9F7AEA] shrink-0" />
                    <p className="text-base font-bold">{p.text}</p>
                  </div>
                ))}
              </div>
              <div className="mt-5 flex gap-3">
                <button data-testid="story-regenerate-button" onClick={() => { setStoryDraft(null); }} className="min-h-[56px] px-5 rounded-full border-2 border-foreground/25 font-bold hover:bg-secondary transition-colors duration-200">
                  Try again
                </button>
                <button data-testid="story-save-button" onClick={saveStory} disabled={storyBusy} className="flex-1 min-h-[56px] rounded-full bg-primary text-white text-lg font-bold flex items-center justify-center gap-2 disabled:opacity-60">
                  <Check size={22} strokeWidth={3} /> {storyBusy ? "Saving…" : "Save story"}
                </button>
              </div>
            </>
          )}
        </Modal>
      )}

      {circleFor && <CareCircleModal profile={circleFor} onClose={() => setCircleFor(null)} />}

      {showDeleteModal && (
        <Modal title="Delete account" onClose={() => setShowDeleteModal(false)} testId="delete-account-modal">
          <p className="text-lg font-semibold text-[#C53030]">This cannot be undone. All profiles, routines, boards, choice boards and care notes will be permanently deleted.</p>
          <label htmlFor="delete-password" className="block text-base font-bold mt-5 mb-1">Confirm your password</label>
          <input
            id="delete-password"
            type="password"
            data-testid="delete-password-input"
            value={deletePassword}
            onChange={(e) => setDeletePassword(e.target.value)}
            className="w-full min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg focus:outline-none focus:border-primary"
          />
          <button
            data-testid="confirm-delete-account-button"
            onClick={deleteAccount}
            disabled={!deletePassword}
            className="mt-5 w-full min-h-[56px] rounded-full bg-[#C53030] text-white text-lg font-bold disabled:opacity-60"
          >
            Permanently delete everything
          </button>
        </Modal>
      )}

      {ftPickerFor && (
        <Modal title="Pick an icon" onClose={() => setFtPickerFor(null)} testId="ft-icon-picker-modal">
          <IconPicker
            value={ft[`${ftPickerFor}_icon`]}
            onChange={(name) => { setFt((f) => ({ ...f, [`${ftPickerFor}_icon`]: name })); setFtPickerFor(null); }}
          />
        </Modal>
      )}

      {pinModal && <PinModal mode={pinModal} onClose={() => setPinModal(null)} />}

      {showProfileModal && (
        <Modal title={editingProfile ? "Rename person" : "Add a person"} onClose={() => setShowProfileModal(false)} testId="profile-modal">
          <label htmlFor="profile-name" className="block text-base font-bold mb-1">Their name</label>
          <input
            id="profile-name"
            data-testid="profile-name-input"
            value={profileName}
            onChange={(e) => setProfileName(e.target.value)}
            placeholder="e.g. Sam"
            className="w-full min-h-[56px] rounded-2xl border-2 border-foreground/25 px-4 text-lg focus:outline-none focus:border-primary"
          />
          <p className="mt-4 mb-2 text-base font-bold">Pick a colour</p>
          <div className="flex gap-3">
            {PROFILE_COLORS.map((c) => (
              <button
                key={c}
                data-testid={`profile-color-${c}`}
                onClick={() => setProfileColor(c)}
                aria-label={`Colour ${c}`}
                className={`w-14 h-14 rounded-full border-4 transition-transform duration-200 ease-out ${profileColor === c ? "border-foreground scale-110" : "border-transparent"}`}
                style={{ background: COLOR_BG[c] }}
              />
            ))}
          </div>
          <button
            data-testid="save-profile-button"
            onClick={saveProfile}
            disabled={!profileName.trim()}
            className="mt-6 w-full min-h-[56px] rounded-full bg-primary text-white text-lg font-bold disabled:opacity-60"
          >
            {editingProfile ? "Save name" : "Save person"}
          </button>
        </Modal>
      )}
    </div>
  );
}
